import type { InputMaybe, Order_By } from '@graphql/__generated__/graphql';
import type {
  CrmManualSelectionRow,
  ExternalManualSelectionRow,
  MergedTransactionRow,
} from '@modules/reports/cma-reports/step-transaction/types';

export type OrderByObject = {
  [key: string]: OrderByObject | InputMaybe<Order_By>;
};

const flattenObject = (
  obj: OrderByObject,
  parentKey?: string,
): Record<string, string> => {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(obj)) {
    const fullKey = parentKey ? `${parentKey}.${key}` : key;
    if (value != null && typeof value === 'object') {
      Object.assign(result, flattenObject(value, fullKey));
    } else if (typeof value === 'string') {
      result[fullKey] = value;
    }
  }
  return result;
};

type SortPair = { path: string[]; direction: string };

const extractSortPairs = (orderBy: OrderByObject[]): SortPair[] =>
  orderBy.flatMap(entry =>
    Object.entries(flattenObject(entry)).map(([dotPath, direction]) => ({
      path: dotPath.split('.'),
      direction,
    })),
  );

const resolvePathValue = (
  row: MergedTransactionRow,
  path: string[],
): unknown => {
  let cursor: unknown = row;
  for (const segment of path) {
    if (cursor == null || typeof cursor !== 'object') {
      return null;
    }
    cursor = (cursor as Record<string, unknown>)[segment];
  }
  return cursor ?? null;
};

const compareValues = (a: unknown, b: unknown): number => {
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }
  if (typeof a === 'string' && typeof b === 'string') {
    return a.localeCompare(b);
  }
  return String(a).localeCompare(String(b));
};

const buildComparator =
  (pairs: SortPair[]) =>
  (a: MergedTransactionRow, b: MergedTransactionRow): number => {
    for (const { path, direction } of pairs) {
      const aVal = resolvePathValue(a, path);
      const bVal = resolvePathValue(b, path);
      const aNull = aVal == null;
      const bNull = bVal == null;

      if (aNull && bNull) {
        continue;
      }

      if (aNull || bNull) {
        const nullsFirst =
          direction === 'asc_nulls_first' ||
          direction === 'desc_nulls_first' ||
          direction === 'desc';
        if (aNull) {
          return nullsFirst ? -1 : 1;
        }
        return nullsFirst ? 1 : -1;
      }

      const cmp = compareValues(aVal, bVal);
      if (cmp !== 0) {
        return direction.startsWith('desc') ? -cmp : cmp;
      }
    }
    return 0;
  };

const DEFAULT_SORT: SortPair[] = [
  { path: ['distance_in_meters'], direction: 'asc' },
];

const EXTERNAL_SORTABLE_FIELDS = new Set([
  'purchase_price',
  'signature_date',
  'living_surface',
  'land_surface',
  'number_of_rooms',
  'distance_in_meters',
]);

export const getExternalPrimarySort = (
  orderBy?: OrderByObject[],
): { field: string; direction: 'asc' | 'desc' } | null => {
  const pair = extractSortPairs(orderBy ?? []).find(({ path }) =>
    EXTERNAL_SORTABLE_FIELDS.has(path[path.length - 1]),
  );

  if (!pair) {
    return null;
  }

  return {
    field: pair.path[pair.path.length - 1],
    direction: pair.direction.startsWith('desc') ? 'desc' : 'asc',
  };
};

export const mergeManualSelectionRows = (
  crmRows: CrmManualSelectionRow[],
  externalRows: ExternalManualSelectionRow[],
  orderBy?: OrderByObject[],
): MergedTransactionRow[] => {
  const pairs = orderBy ? extractSortPairs(orderBy) : [];
  return [...crmRows, ...externalRows].sort(
    buildComparator(pairs.length > 0 ? pairs : DEFAULT_SORT),
  );
};
